import React, { type ChangeEvent, useState } from 'react'

import { LntInput } from '@app/components/base'
import { TopFilters as TopFiltersWrapper, ExcludedFilterSelect } from '@app/components'

import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { LOCATION_KEY, MONITORINGS_LISTS_KEY, MONITORINGS_TYPES_KEY } from '@app/constants/queryKeys'

import { getKinds, getTypes } from '@app/api/routes/monitorings'
import { getStores } from '@app/api/routes/locationStores'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getClusterGroups } from '@app/api/routes/locationClusterGroups'
import { getSalesOrganization } from '@app/api/routes/locationSalesOrganization'
import { getMonitoringListsPromo } from '@app/api/routes/monitoringListsPromo'

import { useFiltersSearch } from '@app/hooks'
import { getFilterFromEntities, getFilterFromEntitiesToLabelsIds } from '@app/utils/entitiesAdapters'

import { SIZE } from '@app/constants'
import { stringWithComma } from '@app/utils/string'
import { updateFiltersByKey } from '@app/pages/ProcessingResultsPage/ui/TopFilters/utils'

import { commonSelectProps } from './configs'

import type { FiltersParams } from '@app/types/filter'
import type { TopFilterProps } from './types'

export const TopFilters: React.FC<TopFilterProps> = ({ onSearch, initialFilters }) => {
  const { filters, setFilters, canResetFilters, handleClearFilters, handleSearch } = useFiltersSearch({
    initialFilters,
    onSearch
  })

  const [parsedProductCodeValue, setParsedProductCodeValue] = useState(filters?.productCodes)

  const handleExcludedSelectChange = (values: FiltersParams): void => {
    setFilters(prev => ({ ...prev, ...values }))
  }

  const onChangeInputProductCode = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value

    setParsedProductCodeValue(value)

    const parsedString = stringWithComma(value)

    setFilters(prev => updateFiltersByKey(prev, FILTERS_PARAMS_KEYS.productCodes, parsedString))
  }

  return (
    <TopFiltersWrapper
      canReset={ canResetFilters }
      onClear={ handleClearFilters }
      onSearch={ handleSearch }
    >
      <ExcludedFilterSelect
        { ...commonSelectProps }
        value={ filters.monitoringKindIds ?? filters.excludedMonitoringKindIds ?? [] }
        defaultIsExcluded={ !!filters.excludedMonitoringKindIds }
        paramKeys={ {
          key: FILTERS_PARAMS_KEYS.monitoringKindIds,
          excludedKey: FILTERS_PARAMS_KEYS.excludedMonitoringKindIds
        } }
        onChange={ handleExcludedSelectChange }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsKindFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.kindsItems],
          queryFn: getKinds,
          select: getFilterFromEntities
        } }
      />

      <ExcludedFilterSelect
        { ...commonSelectProps }
        value={ filters.monitoringTypeIds ?? filters.excludedMonitoringTypeIds ?? [] }
        defaultIsExcluded={ !!filters.excludedMonitoringTypeIds }
        paramKeys={ {
          key: FILTERS_PARAMS_KEYS.monitoringTypeIds,
          excludedKey: FILTERS_PARAMS_KEYS.excludedMonitoringTypeIds
        } }
        onChange={ handleExcludedSelectChange }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.typesItems],
          queryFn: getTypes,
          select: getFilterFromEntities
        } }
      />

      <ExcludedFilterSelect
        { ...commonSelectProps }
        value={ filters.storeIds ?? filters.excludedStoreIds ?? [] }
        defaultIsExcluded={ !!filters.excludedStoreIds }
        paramKeys={ {
          key: FILTERS_PARAMS_KEYS.storeIds,
          excludedKey: FILTERS_PARAMS_KEYS.excludedStoreIds
        } }
        onChange={ handleExcludedSelectChange }
        placeholder={ FILTERS_PLACEHOLDERS.storesFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationStoresItems],
          queryFn: getStores,
          select: getFilterFromEntities
        } }
      />

      <ExcludedFilterSelect
        { ...commonSelectProps }
        value={ filters.clusterIds ?? filters.excludedClusterIds ?? [] }
        defaultIsExcluded={ !!filters.excludedClusterIds }
        paramKeys={ {
          key: FILTERS_PARAMS_KEYS.clusterIds,
          excludedKey: FILTERS_PARAMS_KEYS.excludedClusterIds
        } }
        onChange={ handleExcludedSelectChange }
        placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClustersItems],
          queryFn: getClusters,
          select: getFilterFromEntities
        } }
      />

      <ExcludedFilterSelect
        { ...commonSelectProps }
        value={ filters.clustersGroupIds ?? filters.excludedClustersGroupIds ?? [] }
        defaultIsExcluded={ !!filters.excludedClustersGroupIds }
        paramKeys={ {
          key: FILTERS_PARAMS_KEYS.clustersGroupIds,
          excludedKey: FILTERS_PARAMS_KEYS.excludedClustersGroupIds
        } }
        onChange={ handleExcludedSelectChange }
        placeholder={ FILTERS_PLACEHOLDERS.clusterGroupsFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClusterGroupsItems],
          queryFn: getClusterGroups,
          select: getFilterFromEntities
        } }
      />

      <ExcludedFilterSelect
        { ...commonSelectProps }
        value={ filters.promoIds ?? filters.excludedPromoIds ?? [] }
        defaultIsExcluded={ !!filters.excludedPromoIds }
        paramKeys={ {
          key: FILTERS_PARAMS_KEYS.promoIds,
          excludedKey: FILTERS_PARAMS_KEYS.excludedPromoIds
        } }
        onChange={ handleExcludedSelectChange }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringListsPromoFilter }
        querySelect={ {
          queryKey: [MONITORINGS_LISTS_KEY.promotion],
          queryFn: getMonitoringListsPromo,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <ExcludedFilterSelect
        { ...commonSelectProps }
        value={ filters.salesOrganizationIds ?? filters.excludedSalesOrganizationIds ?? [] }
        defaultIsExcluded={ !!filters.excludedSalesOrganizationIds }
        paramKeys={ {
          key: FILTERS_PARAMS_KEYS.salesOrganizationIds,
          excludedKey: FILTERS_PARAMS_KEYS.excludedSalesOrganizationIds
        } }
        onChange={ handleExcludedSelectChange }
        placeholder={ FILTERS_PLACEHOLDERS.salesOrganizationFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationSalesOrganizationsItems],
          queryFn: getSalesOrganization,
          select: getFilterFromEntities
        } }
      />

      <LntInput
        value={ parsedProductCodeValue }
        onChange={ event => onChangeInputProductCode(event) }
        placeholder={ FILTERS_PLACEHOLDERS.productCode }
        size={ SIZE.middle }
      />
    </TopFiltersWrapper>
  )
}
